const elementName = 'savannah-collection-view-more';
const allowedCollections = new Set(['health-beauty', 'honey']);

class SavannahCollectionViewMore extends HTMLElement {
  #connectionController;
  #button;
  #fallbackPagination;
  #grid;
  #pending = false;

  connectedCallback() {
    this.#teardown();

    const collectionHandle = this.dataset.collectionHandle || '';
    const resultsList = this.closest('results-list');
    const button = this.querySelector('[data-savannah-view-more-button]');
    const grid = resultsList?.querySelector('[ref="grid"]');
    const fallbackPagination = resultsList?.querySelector('.pagination');
    const currentPage = Number(this.dataset.currentPage);
    const lastPage = Number(this.dataset.lastPage);

    if (
      !allowedCollections.has(collectionHandle) ||
      !(button instanceof HTMLButtonElement) ||
      !(grid instanceof HTMLUListElement) ||
      !(fallbackPagination instanceof HTMLElement) ||
      !Number.isInteger(currentPage) ||
      !Number.isInteger(lastPage) ||
      currentPage < 1 ||
      lastPage <= currentPage ||
      !this.dataset.sectionId ||
      !this.dataset.nextUrl
    ) {
      return;
    }

    this.#button = button;
    this.#grid = grid;
    this.#fallbackPagination = fallbackPagination;
    this.#connectionController = new AbortController();
    this.#button.addEventListener('click', this.#loadNextPage, {
      signal: this.#connectionController.signal
    });
    this.#fallbackPagination.hidden = true;
    this.#button.hidden = false;
    this.dataset.enhanced = 'true';
  }

  disconnectedCallback() {
    this.#teardown();
  }

  #teardown() {
    this.#connectionController?.abort();
    this.#connectionController = undefined;
    this.#pending = false;

    if (this.#fallbackPagination?.isConnected) {
      this.#fallbackPagination.hidden = false;
    }
    if (this.#button) {
      this.#button.hidden = true;
      this.#button.disabled = false;
      this.#button.removeAttribute('aria-busy');
    }

    this.#button = undefined;
    this.#fallbackPagination = undefined;
    this.#grid = undefined;
    delete this.dataset.enhanced;
  }

  #failOpen() {
    if (this.#fallbackPagination) {
      this.#fallbackPagination.hidden = false;
    }
    if (this.#button) {
      this.#button.hidden = true;
      this.#button.disabled = false;
      this.#button.removeAttribute('aria-busy');
    }
    this.dataset.state = 'fallback';
  }

  #loadNextPage = async (event) => {
    event.preventDefault();
    if (
      this.#pending ||
      !this.isConnected ||
      !this.#connectionController ||
      !this.#button ||
      !this.#grid
    ) {
      return;
    }

    const currentPage = Number(this.dataset.currentPage);
    const lastPage = Number(this.dataset.lastPage);
    const sectionId = this.dataset.sectionId || '';
    const nextUrl = new URL(this.dataset.nextUrl || '', window.location.href);
    const nextPage = Number(nextUrl.searchParams.get('page'));

    if (
      nextUrl.origin !== window.location.origin ||
      nextUrl.pathname !== window.location.pathname ||
      !Number.isInteger(nextPage) ||
      nextPage !== currentPage + 1 ||
      nextPage > lastPage
    ) {
      this.#failOpen();
      return;
    }

    nextUrl.hash = '';
    nextUrl.searchParams.set('section_id', sectionId);
    this.#pending = true;
    this.#button.disabled = true;
    this.#button.setAttribute('aria-busy', 'true');
    this.dataset.state = 'loading';

    try {
      const response = await fetch(nextUrl, {
        method: 'GET',
        headers: {
          accept: 'text/html'
        },
        credentials: 'same-origin',
        redirect: 'error',
        signal: this.#connectionController.signal
      });
      const contentType = response.headers.get('content-type') || '';
      if (!response.ok || !contentType.toLowerCase().includes('text/html')) {
        throw new Error('SAVANNAH_COLLECTION_PAGE_RESPONSE_INVALID');
      }

      const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
      const sourceResultsList = [...documentFragment.querySelectorAll('results-list')].find(
        (element) => element.getAttribute('section-id') === sectionId
      );
      const sourceGrid = sourceResultsList?.querySelector('[ref="grid"]');
      const sourceController = sourceResultsList?.querySelector(elementName);
      if (
        !(sourceGrid instanceof HTMLUListElement) ||
        !(sourceController instanceof HTMLElement)
      ) {
        throw new Error('SAVANNAH_COLLECTION_PAGE_MARKUP_INVALID');
      }

      const sourceCards = [...sourceGrid.children].filter(
        (element) => element.getAttribute('ref') === 'cards[]'
      );
      if (
        sourceCards.length === 0 ||
        sourceCards.some((card) => Number(card.dataset.page) !== nextPage)
      ) {
        throw new Error('SAVANNAH_COLLECTION_PAGE_CARDS_INVALID');
      }

      const existingProductIds = new Set(
        [...this.#grid.children]
          .map((card) => card.dataset.productId)
          .filter(Boolean)
      );
      if (
        sourceCards.some(
          (card) => !card.dataset.productId || existingProductIds.has(card.dataset.productId)
        )
      ) {
        throw new Error('SAVANNAH_COLLECTION_PAGE_DUPLICATE_CARD');
      }

      const importedCards = sourceCards.map((card) => document.importNode(card, true));
      if (!this.isConnected || this.#connectionController.signal.aborted) {
        return;
      }
      this.#grid.append(...importedCards);
      this.dataset.currentPage = String(nextPage);
      this.dataset.lastPage = sourceController.dataset.lastPage || String(lastPage);
      this.dataset.nextUrl = sourceController.dataset.nextUrl || '';
      this.dataset.state = 'ready';

      const hasNextPage =
        Number(this.dataset.currentPage) < Number(this.dataset.lastPage) &&
        Boolean(this.dataset.nextUrl);
      this.#button.hidden = !hasNextPage;
      this.#button.disabled = false;
      this.#button.removeAttribute('aria-busy');

      this.dispatchEvent(
        new CustomEvent('savannah:collection-page-appended', {
          bubbles: true,
          detail: {
            page: nextPage,
            productCount: importedCards.length
          }
        })
      );
    } catch (error) {
      if (error?.name !== 'AbortError' && this.isConnected) {
        this.#failOpen();
      }
    } finally {
      this.#pending = false;
      if (this.#button && this.isConnected && !this.#button.hidden) {
        this.#button.disabled = false;
        this.#button.removeAttribute('aria-busy');
      }
    }
  };
}

if (!customElements.get(elementName)) {
  customElements.define(elementName, SavannahCollectionViewMore);
}
